import { supabase } from './supabase';

export async function getAccessibleClientsForUser(userId) {
  const [{ data: cliRows }, { data: regRows }] = await Promise.all([
    supabase.from('admin_user_clients').select('client_id').eq('user_id', userId),
    supabase.from('admin_user_regions').select('region_id').eq('user_id', userId),
  ]);

  const clientIds = new Set((cliRows || []).map((r) => String(r.client_id)));
  const clientScopeIds = new Set((cliRows || []).map((r) => String(r.client_id)));
  const regionIds = (regRows || []).map((r) => r.region_id);
  if (regionIds.length > 0) {
    const { data: regionMap } = await supabase
      .from('regions')
      .select('id, client_id')
      .in('id', regionIds);
    (regionMap || []).forEach((r) => clientIds.add(String(r.client_id)));
  }

  const ids = Array.from(clientIds).filter(Boolean);
  if (ids.length === 0) return [];

  const { data: clients } = await supabase
    .from('clients')
    .select('id, business_name')
    .in('id', ids);

  return (clients || [])
    .map((c) => ({ ...c, hasClientScope: clientScopeIds.has(String(c.id)) }))
    .slice()
    .sort((a, b) => String(a.business_name || '').localeCompare(String(b.business_name || '')));
}

export async function getRegionsForClient(userId, clientId) {
  const [{ data: cliRows }, { data: regRows }] = await Promise.all([
    supabase.from('admin_user_clients').select('client_id').eq('user_id', userId),
    supabase.from('admin_user_regions').select('region_id').eq('user_id', userId),
  ]);

  const hasClientScope = (cliRows || []).some((r) => String(r.client_id) === String(clientId));
  let allowedRegionIds = null;

  if (!hasClientScope) {
    allowedRegionIds = new Set();
    const regionIds = (regRows || []).map((r) => r.region_id);
    if (regionIds.length > 0) {
      const { data: regionMap } = await supabase
        .from('regions')
        .select('id, client_id')
        .in('id', regionIds);
      (regionMap || []).forEach((r) => {
        if (String(r.client_id) === String(clientId)) allowedRegionIds.add(String(r.id));
      });
    }
    if (allowedRegionIds.size === 0) return { regions: [], hasClientScope: false };
  }

  const { data: regionsData } = await supabase
    .from('regions')
    .select('id, name, active')
    .eq('client_id', clientId)
    .eq('active', true);

  const visible = (regionsData || []).filter(
    (r) => hasClientScope || allowedRegionIds.has(String(r.id))
  );

  return {
    regions: visible.map((r) => ({ id: r.id, name: r.name })),
    hasClientScope,
  };
}

export async function getTeamsForRegion(clientId, regionId) {
  const { data: teamsData } = await supabase
    .from('teams')
    .select('id, name, region_id, active')
    .eq('client_id', clientId)
    .eq('region_id', regionId)
    .eq('active', true);

  return (teamsData || []).map((t) => ({ id: t.id, name: t.name }));
}

export async function getUsersForTeam(teamId) {
  const { data: teamUsers } = await supabase
    .from('team_users')
    .select('team_id, user_id')
    .eq('team_id', teamId);

  const userIds = Array.from(new Set((teamUsers || []).map((tu) => tu.user_id)));
  if (userIds.length === 0) return [];

  const { data: usersData } = await supabase
    .from('users')
    .select('id, full_name, email')
    .in('id', userIds);

  return (usersData || []).map((u) => ({
    id: String(u.id),
    full_name: u.full_name,
    email: u.email,
    displayName: u.full_name || u.email || 'Unknown user',
  }));
}

export async function getTeamDetail(clientId, regionId, teamId) {
  const { data: teamsData } = await supabase
    .from('teams')
    .select('id, name, region_id, active')
    .eq('id', teamId)
    .eq('client_id', clientId)
    .eq('region_id', regionId)
    .maybeSingle();

  if (!teamsData) return null;

  const users = await getUsersForTeam(teamId);
  const teamIds = [teamId];
  const now = Date.now();

  const { data: bootcamps } = await supabase
    .from('bootcamps')
    .select('id, start_date, end_date, team_id, courses(name), teams(name)')
    .in('team_id', teamIds);

  const team = {
    ...teamsData,
    users: [],
    activeBootcamp: null,
    upcomingBootcamp: null,
    latestCompletedBootcamp: null,
    bootcamps: [],
  };

  team.users = users;

  team.bootcamps = bootcamps || [];
  const classified = classifyBootcamps(team.bootcamps, now);
  team.activeBootcamp = classified.activeBootcamp;
  team.upcomingBootcamp = classified.upcomingBootcamp;
  team.latestCompletedBootcamp = classified.latestCompletedBootcamp;
  team.bootcamps = classified.sorted;

  return team;
}

function classifyBootcamps(bootcamps, nowMs = Date.now()) {
  let activeBootcamp = null;
  let upcomingBootcamp = null;
  let latestCompletedBootcamp = null;
  const sorted = (bootcamps || []).slice().sort(
    (a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
  );

  sorted.forEach((bc) => {
    const startTs = new Date(bc.start_date).getTime();
    const endTs = new Date(bc.end_date).getTime();
    if (startTs <= nowMs && endTs > nowMs) {
      activeBootcamp = bc;
    } else if (startTs > nowMs) {
      if (!upcomingBootcamp || new Date(upcomingBootcamp.start_date).getTime() > startTs) {
        upcomingBootcamp = bc;
      }
    } else if (endTs <= nowMs) {
      if (!latestCompletedBootcamp || new Date(latestCompletedBootcamp.end_date).getTime() < endTs) {
        latestCompletedBootcamp = bc;
      }
    }
  });

  return { activeBootcamp, upcomingBootcamp, latestCompletedBootcamp, sorted };
}

export function pickPreferredBootcamp(bootcamps, nowMs = Date.now()) {
  const { activeBootcamp, upcomingBootcamp, latestCompletedBootcamp, sorted } = classifyBootcamps(bootcamps, nowMs);
  return activeBootcamp || upcomingBootcamp || latestCompletedBootcamp || sorted[0] || null;
}

export async function getPreferredBootcampIdForTeam(teamId) {
  const { data: bootcamps, error } = await supabase
    .from('bootcamps')
    .select('id, start_date, end_date')
    .eq('team_id', teamId);
  if (error) throw error;
  const preferred = pickPreferredBootcamp(bootcamps || []);
  return preferred?.id ?? null;
}

const SELECTION_KEY = 'manager.selection.v1';

export function readManagerSelection() {
  try {
    const raw = sessionStorage.getItem(SELECTION_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function writeManagerSelection(selection) {
  try {
    sessionStorage.setItem(SELECTION_KEY, JSON.stringify(selection));
  } catch {}
}

const ADMIN_ANALYTICS_SELECTION_KEY = 'admin.analytics.selection.v1';

export function readAdminAnalyticsSelection() {
  try {
    const raw = sessionStorage.getItem(ADMIN_ANALYTICS_SELECTION_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function writeAdminAnalyticsSelection(selection) {
  try {
    sessionStorage.setItem(ADMIN_ANALYTICS_SELECTION_KEY, JSON.stringify(selection));
  } catch {}
}

export async function getAllClients() {
  const { data: clients } = await supabase
    .from('clients')
    .select('id, business_name')
    .order('business_name');

  return (clients || [])
    .map((c) => ({ ...c, hasClientScope: true }))
    .slice()
    .sort((a, b) => String(a.business_name || '').localeCompare(String(b.business_name || '')));
}

export async function getAllRegionsForClient(clientId) {
  const { data: regionsData } = await supabase
    .from('regions')
    .select('id, name, active')
    .eq('client_id', clientId)
    .eq('active', true);

  return {
    regions: (regionsData || []).map((r) => ({ id: r.id, name: r.name })),
    hasClientScope: true,
  };
}
